import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  ViewChild,
  signal,
  computed,
  HostListener,
  ElementRef,
  Renderer2,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import {
  auditTime,
  combineLatest,
  Subject,
  takeUntil,
  distinctUntilChanged,
  take,
  Subscription,
  catchError,
  EMPTY,
  Observable,
  defer,
  of,
  switchMap,
  map,
  delay,
  filter as rxFilter,
} from 'rxjs';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  Router,
} from '@angular/router';
import { PrimecustomModule } from 'src/app/shared/primeng.custom.module';
import { Popover } from 'primeng/popover';
import { COMMON_SVG_IMG_PATH } from 'src/app/shared/common.constant';
import { CommonService } from 'src/app/shared/service/common.service';
import { ReportService } from 'src/app/features/reports/report.service';
import {
  FilterConfig,
  FilterDateOptions,
  Option,
} from './universal-filter.interface';
import { RoleService } from 'src/app/shared/service/role.service';
import { PayloadTransformService } from 'src/app/shared/service/payload-transform.service';
import { Store } from '@ngrx/store';
import { selectSelectedVehicle } from 'src/app/store/user/user.selectors';
import * as FILTER_CONFIG from './universal-filter.config';
import * as UniversalFilterActions from 'src/app/store/universalFilter/universal-filter.actions';
import {
  selectFilterConfig,
  selectFilterDateOptions,
  selectUniversalFilters,
} from 'src/app/store/universalFilter/universal-filter.selectors';
import moment from 'moment';
import { calenderDateEdgeColor } from 'src/app/shared/utils/common.utils';
import { TimeSelectionComponent } from '../time-selection/time-selection.component';
import { UniversalFilterHelperService } from 'src/app/shared/component/filter/universal-filter/universal-filter-helper.service';
import { UniversalFilterUtilityService } from './universal-filter-utility.service';
import { UtilService } from 'src/app/core/services/util.service';
import { SsoService } from 'src/app/core/services/sso.service';
import {
  FILTER_APPLICATION,
  FILTER_DEMAND_GENERATOR,
  FILTER_BATTERY_CAPACITY,
} from 'src/app/shared/api.constant';
import { MessageService } from 'primeng/api';
import {
  MonthRangePickerComponent,
  MonthRange,
} from '../../monthly-range-picker/monthly-range-picker.component';
export interface FilterOption {
  code: string;
  name: string;
}
export interface FilterPagination {
  pageNumber: number;
  totalRecords: number;
  pageSize: number;
}

export interface FilterSection {
  title: string;
  filterName: string;
  placeholder: string;
  options: FilterOption[];
  type?: 'text' | 'select' | 'date';
  icon?: string;
  multiple?: boolean;
  group?: 'location' | 'vehicle' | 'sourceSpec';
  isLoading: boolean;
  searchTerm: string;
  methodNameEnding: string; // e.g., 'Customer' for loadCustomer
  pagination: FilterPagination;
  groupBy?: string; // Optional property for grouping in ng-select
}

@Component({
  selector: 'app-universal-filter',
  standalone: true,
  imports: [
    PrimecustomModule,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TimeSelectionComponent,
    MonthRangePickerComponent,
  ],
  templateUrl: './universal-filter.component.html',
  styleUrl: './universal-filter.component.scss',
})
export class UniversalFilterComponent implements OnInit, OnDestroy {
  @ViewChild('advancedPanel') advancedPanel!: Popover;
  @ViewChild('overlayWrapper') overlayWrapper!: ElementRef;
  private removeDocumentListener!: () => void;
  // main signal for filter sections
  filterSections = signal<FilterSection[]>([]);
  // signal to track if any overlay (grouped) filters have been applied
  hasOverlayFiltersApplied = signal(false);
  // derived signals
  locationFilters = computed(() =>
    this.filterSections().filter((s) => s.group === 'location')
  );

  vehicleFilters = computed(() =>
    this.filterSections().filter((s) => s.group === 'vehicle')
  );

  sourceSpecFilters = computed(() =>
    this.filterSections().filter((s) => s.group === 'sourceSpec')
  );

  activeColumnCount = computed(() => {
    let count = 0;
    if (this.locationFilters().length > 0) count++;
    if (this.vehicleFilters().length > 0) count++;
    if (this.sourceSpecFilters().length > 0) count++;
    return count;
  });

  headerSections = computed<FilterSection[]>(() =>
    this.filterSections().filter((s) => !s.group)
  );

  debugSections = computed(
    () =>
      this.filterSections().find((s) => s.filterName === 'region')?.options
        .length
  ); // Example for debugging

  @Output() filterApply = new EventEmitter<Record<string, any>>();
  @Output() filterClear = new EventEmitter<void>();
  @Output() filterChange = new EventEmitter<{
    filterName: string;
    value: any;
  }>();
  @Input() filterConfig: FilterConfig = {
    role: false,
    monthlyCalender: false,
  };

  filterDateOptions: FilterDateOptions = {
    options: [],
    min: new Date(),
    max: new Date(),
    days: 0,
    totalOption: 0,
  };

  /** Single-item option list used when the date filter is disabled (date: false).
   *  Rendering a controlled dropdown with only "Today" keeps the UI consistent
   *  while preventing the user from selecting any other date. */
  readonly todayOnlyOptions: Option[] = [
    { code: 'Today', name: 'Today', id: 'Today', default: true },
  ];

  filterForm!: FormGroup;
  isAdvancedOpen = false;
  svgPath: string = COMMON_SVG_IMG_PATH;

  /** Date picker state */
  customDate = false;
  selectedDate: any = [];
  defaultDate: any = [null, null];
  diff = 0;
  showTimePicker = false;

  /** Monthly calendar picker state */
  // currentMonth: Date = new Date();
  currentMonth: Date = moment().startOf('month').toDate();
  minDateCalender: Date = moment().subtract(6, 'months').toDate();
  maxDateCalender: Date = new Date();

  /** Pagination properties */
  bufferSize = 50;
  numberOfItemsFromEndBeforeFetchingMore = 10;

  pageNumber = 1;
  totalRecords = 0;
  pageSize = 50;

  regions: any;
  dealers: any;
  customer: any;
  customerSingle: any;
  states: any;
  citys: any;
  fleets: any;
  statusOptions: any = [];
  vehicle: any = [];
  vehiclesingle: any;
  models: any;
  variants: any;

  /** Selected filters for API calls */
  selectedFilters: any = {
    region: '',
    state: '',
    city: '',
    dealer: '',
    customer: '',
    fleet: '',
    vehicle: '',
    model: '',
    variant: '',
    application: '',
    demandGenerator: '',
    batteryCapacity: '',
  };
  [key: string]: any;
  private unsubscribe$ = new Subject<void>();
  /** True while the selected Region was auto-picked by preselectRegion, not by the user. */
  private get autoPreselectedRegion(): boolean {
    return this.universalFilterUtility.autoPreselectedRegion;
  }
  private set autoPreselectedRegion(value: boolean) {
    this.universalFilterUtility.autoPreselectedRegion = value;
  }
  /** In-flight combined option fetch; cancelled when a newer one starts. */
  private combinedDataSub?: Subscription;
  isCustomerView = false;
  isDealerView = false;
  isCustomerOrDealerView = false;
  selectedVehicleType!: any;
  showUniversalFilter = false;

  constructor(
    private readonly commonService: CommonService,
    private readonly reportService: ReportService,
    private readonly roleService: RoleService,
    private readonly payloadTransformService: PayloadTransformService,
    private readonly store: Store,
    private readonly universalFilterHelperService: UniversalFilterHelperService,
    private readonly universalFilterUtility: UniversalFilterUtilityService,
    private readonly utilService: UtilService,
    private readonly ssoService: SsoService,
    private renderer: Renderer2,
    private readonly messageService: MessageService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.isCustomerView = this.roleService.isCustomerView;
    this.isDealerView = this.roleService.isDealerView;
    this.isCustomerOrDealerView = this.isCustomerView || this.isDealerView;
    this.initializeVehicleType();

    this.utilService.hasUniversalFilter().subscribe((showFilter) => {
      this.showUniversalFilter = showFilter;

      if (this.showUniversalFilter) {
        // Subscribe to the store-driven filter config so any feature component
        // can declaratively configure which filters are active.
        combineLatest([
          this.store.select(selectFilterConfig),
          this.store.select(selectFilterDateOptions),
        ])
          .pipe(
            // While a route transition is running, the target page has not yet
            // declared its filter config (the header is created first on a
            // reload, and the old page clears it while leaving). Reacting now
            // would load the dropdowns for a config that is replaced a moment
            // later, so wait for the transition to finish.
            switchMap((value) =>
              this.whenRouteSettled().pipe(map(() => value))
            ),
            auditTime(0),
            takeUntil(this.unsubscribe$),
            distinctUntilChanged(
              (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
            )
          )
          .subscribe(([config, dateOptions]) => {
            // Merge store config with the @Input config (store wins when set)
            // When the store config was cleared (page left), fall back to the
            // previous config without its page-specific Region preselect.
            const activeConfig =
              Object.keys(config).length > 0
                ? config
                : { ...this.filterConfig, preselectRegion: false };
            this.filterConfig = { ...activeConfig };

            // Moving to a screen that doesn't preselect Region: drop the region
            // we auto-picked earlier so it isn't restored from the store below.
            if (
              !this.filterConfig.preselectRegion &&
              this.autoPreselectedRegion
            ) {
              this.autoPreselectedRegion = false;
              this.store.dispatch(
                UniversalFilterActions.updateUniversalFilter({
                  filters: { region: null },
                })
              );
            }

            const activeDateOptions =
              dateOptions?.options?.length && dateOptions.totalOption > 0
                ? dateOptions
                : this.filterDateOptions;
            this.filterDateOptions = {
              options: [...(activeDateOptions?.options || [])],
              min: activeDateOptions?.min
                ? new Date(activeDateOptions.min)
                : new Date(),
              max: activeDateOptions?.max
                ? new Date(activeDateOptions.max)
                : new Date(),
              days: activeDateOptions?.days || 0,
              totalOption: activeDateOptions?.totalOption || 0,
            };

            // Reset sections and form so stale controls don't linger
            this.filterSections.set([]);
            this.selectedFilters = {
              region: '',
              state: '',
              city: '',
              dealer: '',
              customer: '',
              fleet: '',
              vehicle: '',
              model: '',
              variant: '',
              application: '',
              demandGenerator: '',
              batteryCapacity: '',
            };

            this.initializeDefaultSections();
            this.buildForm();

            // Restore previously applied filter selections from the store so
            // navigating away and back does not lose the user's chosen values.
            this.store
              .select(selectUniversalFilters)
              .pipe(take(1))
              .subscribe((savedFilters) => {
                if (Object.keys(savedFilters).length > 0) {
                  // Exclude monthlyCalender + its computed date-range metadata
                  // from the generic patchValue call.  The p-datepicker control
                  // requires a Date object – passing the stored ISO string via
                  // patchValue causes PrimeNG to silently ignore / misparse the
                  // value so the picker renders blank.  We restore it separately
                  // as a proper Date below.
                  const {
                    monthlyCalender: savedMonthlyStr,
                    monthlyStartDate: _ms,
                    monthlyEndDate: _me,
                    ...filtersToRestore
                  } = savedFilters as any;

                  this.filterForm.patchValue(filtersToRestore, {
                    emitEvent: false,
                  });

                  // Restore the date form control to the actual option object from
                  // filterDateOptions.options so the p-dropdown can match it by
                  // reference. The stored value may have extra properties
                  // (startDate, endDate) added by getResolvedFilterValues() which
                  // breaks PrimeNG's reference-based option matching.
                  if (this.filterConfig.date) {
                    const savedDate = (savedFilters as any)['date'];
                    if (savedDate?.id || savedDate?.code) {
                      const matchKey = savedDate.id || savedDate.code;
                      let matchedOption = this.filterDateOptions.options.find(
                        (opt: any) =>
                          opt.id === matchKey || opt.code === matchKey
                      );

                      // For custom date ranges (custom1) the option is not in the
                      // preset list — re-add it from the stored value so the
                      // dropdown can display it.
                      if (!matchedOption && matchKey === 'custom1') {
                        const customOption: any = {
                          code: savedDate.code,
                          name: savedDate.name,
                          id: savedDate.id,
                          // Preserve startDate/endDate from the store so
                          // getResolvedFilterValues can use them when the user
                          // clicks Apply without re-opening the calendar
                          // (navigation-back scenario where selectedDate = []).
                          ...(savedDate.startDate && {
                            startDate: savedDate.startDate,
                          }),
                          ...(savedDate.endDate && {
                            endDate: savedDate.endDate,
                          }),
                        };
                        this.filterDateOptions.options = [
                          customOption,
                          ...this.filterDateOptions.options,
                        ];
                        matchedOption = customOption;
                      }

                      // Restore selectedDate array for custom1 so that
                      // getResolvedFilterValues has valid Date objects when the
                      // user clicks Apply without re-opening the calendar picker
                      // (e.g. navigating back to a page that had a custom range).
                      if (
                        matchKey === 'custom1' &&
                        savedDate.startDate &&
                        savedDate.endDate
                      ) {
                        const restoredStart = new Date(savedDate.startDate);
                        const restoredEnd = new Date(savedDate.endDate);
                        if (
                          !isNaN(restoredStart.getTime()) &&
                          !isNaN(restoredEnd.getTime())
                        ) {
                          this.selectedDate = [restoredStart, restoredEnd];
                        }
                      }

                      if (matchedOption) {
                        this.filterForm
                          .get('date')
                          ?.setValue(matchedOption, { emitEvent: false });
                      }
                    }
                  }

                  // Restore monthly calendar as a proper Date object.
                  // Always set directly (never via patchValue) to avoid
                  // PrimeNG receiving a raw ISO string which causes the
                  // picker to render blank.
                  if (this.filterConfig.monthlyCalender) {
                    const restoredRange =
                      this.parseSavedMonthRange(savedMonthlyStr) ??
                      this.getDefaultMonthRange();
                    this.filterForm
                      .get('monthlyCalender')
                      ?.setValue(restoredRange, { emitEvent: false });
                  }

                  // Sync selectedFilters codes so dependent-dropdown loading
                  // (e.g. state→city, customer→fleet) works correctly.
                  Object.keys(savedFilters).forEach((key) => {
                    const val = (savedFilters as any)[key];
                    if (val !== null && val !== undefined) {
                      this.selectedFilters[key] =
                        this.payloadTransformService.extractCodes(val);
                    }
                  });

                  // Re-evaluate the overlay badge after restoring values.
                  const overlayFilterNames = this.filterSections()
                    .filter(
                      (s) =>
                        s.group === 'location' ||
                        s.group === 'vehicle' ||
                        s.group === 'sourceSpec'
                    )
                    .map((s) => s.filterName);
                  const hasOverlay = overlayFilterNames.some((name) => {
                    const val = this.filterForm.get(name)?.value;
                    return (
                      val !== null &&
                      val !== undefined &&
                      (Array.isArray(val) ? val.length > 0 : val !== '')
                    );
                  });
                  this.hasOverlayFiltersApplied.set(hasOverlay);

                  // When the date filter is disabled (date: false / not given),
                  // the generic patchValue above may have restored a stored "Today"
                  // object that carries extra properties (startDate, endDate).
                  // PrimeNG's p-dropdown uses reference matching so it can't match
                  // the stored object against todayOnlyOptions[0]. Reset the form
                  // control to the canonical option so the dropdown renders "Today".
                  if (
                    !this.filterConfig.date &&
                    !this.filterConfig.monthlyCalender
                  ) {
                    this.filterForm
                      .get('date')
                      ?.setValue(this.todayOnlyOptions[0], {
                        emitEvent: false,
                      });
                  }
                } else if (this.filterConfig.monthlyCalender) {
                  this.filterForm
                    .get('monthlyCalender')
                    ?.setValue(this.getDefaultMonthRange(), {
                      emitEvent: false,
                    });
                }

                // When the date filter is disabled (date: false), the UI shows a
                // read-only "Today" dropdown. Ensure this default is also written
                // to the NgRx store on every initialisation (fresh load, refresh,
                // and navigation-back) so dependent components always receive a
                // valid date even when the user has never explicitly applied a
                // filter. The format mirrors what getResolvedFilterValues() produces
                // when date: false, keeping the store shape consistent.
                if (!this.filterConfig.date) {
                  const todayStart = moment().format('YYYY-MM-DD 00:00:00');
                  const todayEnd = moment().format('YYYY-MM-DD 23:59:59');
                  this.store.dispatch(
                    UniversalFilterActions.updateUniversalFilter({
                      filters: {
                        date: {
                          code: 'Today',
                          name: 'Today',
                          id: 'Today',
                          default: true,
                          startDate: todayStart,
                          endDate: todayEnd,
                        },
                      },
                    })
                  );
                }
              });

            if (!this.isCustomerOrDealerView) {
              // OEM / Admin view – full combined data load.
              this.fetchCombinedData(true);
              if (
                this.customerField?.value &&
                this.customerField?.value?.length !== 0
              ) {
                this.fleetField?.enable();
                this.loadFleet('');
              } else {
                this.fleetField?.disable();
              }
            } else if (this.isCustomerView) {
              // Customer view – fleet is immediately available (scoped to the
              // logged-in customer's session; no explicit customer selection needed).
              this.fleetField?.enable();
              this.loadFleet('');
              this.loadVehicle('');
              this.loadModel('');
            } else {
              // Dealer view – load customer + initial vehicle/model data;
              // fleet becomes enabled once the user selects a customer.
              this.loadCustomer('');
              this.loadVehicle('');
              this.loadModel('');
              if (
                this.customerField?.value &&
                this.customerField?.value?.length !== 0
              ) {
                this.fleetField?.enable();
                this.loadFleet('');
              } else {
                this.fleetField?.disable();
              }
            }

            if (
              this.modelField?.value &&
              this.modelField?.value?.length !== 0
            ) {
              this.variantField?.enable();
              this.loadVariant('');
            } else {
              this.variantField?.disable();
            }

            // Load Source and Specifications dropdowns when config flags are set.
            if (this.filterConfig.application) {
              this.loadApplication('');
            }
            if (this.filterConfig.demandGenerator) {
              this.loadDemandGenerator('');
            }
            if (this.filterConfig.batteryCapacity) {
              this.loadBatteryCapacity('');
            }
          });
      }
    });

    this.removeDocumentListener = this.renderer.listen(
      'document',
      'mousedown',
      (event: MouseEvent) => {
        if (!this.isAdvancedOpen) return;

        const target = event.target as HTMLElement;

        const insideOverlay = target.closest('.universal-advanced-panel');
        const insideDropdown =
          target.closest('.ng-dropdown-panel') ||
          target.closest('.ng-select-container');

        const toggleButton = target.closest('.filter-toggle-btn');

        if (insideOverlay || insideDropdown || toggleButton) {
          return;
        }

        this.advancedPanel.hide();
      }
    );
  }

  /**
   * Emits as soon as no route transition is in progress: immediately when idle,
   * otherwise one task after the transition ends, so the new page's ngOnInit has
   * already run and dispatched its filter config.
   */
  private whenRouteSettled(): Observable<void> {
    return defer(() => {
      if (!this.router.currentNavigation()) {
        return of(undefined);
      }
      return this.router.events.pipe(
        rxFilter(
          (event) =>
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError ||
            event instanceof NavigationSkipped
        ),
        take(1),
        delay(0),
        map(() => undefined)
      );
    });
  }

  private initializeVehicleType(): void {
    this.store.select(selectSelectedVehicle).subscribe((vehicle) => {
      this.selectedVehicleType = vehicle || FILTER_CONFIG.vehicleSelescted;
    });
  }

  private initializeDefaultSections(): void {
    if (this.filterSections().length === 0) {
      // Section definitions are maintained in UniversalFilterUtilityService so
      // all OEM-filter-mirrored configuration lives in one place.
      // The view flags drive which sections appear (configuration-driven rendering).
      let sections = this.universalFilterUtility.createSectionsForView(
        this.isCustomerView,
        this.isDealerView
      );

      // Filter out sourceSpec sections whose config flag is not set.
      const sourceSpecConfigMap: Record<string, keyof FilterConfig> = {
        application: 'application',
        demandGenerator: 'demandGenerator',
        batteryCapacity: 'batteryCapacity',
      };
      sections = sections.filter((s) => {
        if (s.group !== 'sourceSpec') return true;
        const configKey = sourceSpecConfigMap[s.filterName];
        return configKey && this.filterConfig[configKey];
      });

      this.filterSections.set(sections);
    }
  }

  /**
   * Build the reactive form dynamically from filterSections
   */
  private buildForm(): void {
    const sections = this.filterSections();
    if (!sections.length) return;

    const controls: Record<string, FormControl> = {};
    sections.forEach((section) => {
      if (section.filterName === 'date') {
        const initialDate = this.filterConfig.date
          ? this.getDefaultDate()
          : this.todayOnlyOptions[0];
        controls[section.filterName] = new FormControl(initialDate);
      } else {
        controls[section.filterName] = new FormControl(null);
      }
    });

    // Add monthlyCalender control when the feature page requires a month picker.
    // Read the persisted month directly from the store so the control is
    // initialised as a Date object immediately – never going through the ISO-string
    // stage that causes PrimeNG's p-datepicker to render blank.
    if (this.filterConfig.monthlyCalender) {
      let initialRange = this.getDefaultMonthRange();
      this.store
        .select(selectUniversalFilters)
        .pipe(take(1))
        .subscribe((filters) => {
          const parsed = this.parseSavedMonthRange(
            (filters as any)?.['monthlyCalender']
          );
          if (parsed) initialRange = parsed;
        });
      controls['monthlyCalender'] = new FormControl(initialRange);
    }

    this.filterForm = new FormGroup(controls);
  }

  /**
   * Returns a FormControl for the given filter name
   */
  getFormControl(filterName: string): FormControl {
    return (
      (this.filterForm.get(filterName) as FormControl) || new FormControl()
    );
  }

  /**
   * TrackBy function for ngFor performance
   */
  trackByFilterName(index: number, section: FilterSection): string {
    return section.filterName;
  }

  private getDefaultSelectedFilters() {
    // Centralised in UniversalFilterUtilityService — mirrors OEM filter default state.
    return this.universalFilterUtility.getDefaultSelectedFilters();
  }

  private resetSelectedFilterState(): void {
    this.selectedFilters = this.getDefaultSelectedFilters();

    this.filterSections.update((sections) =>
      sections.map((section) => ({
        ...section,
        isLoading: false,
        searchTerm: '',
        pagination: {
          ...section.pagination,
          pageNumber: 1,
          totalRecords: 0,
          pageSize: 50,
        },
        options:
          section.filterName === 'city' ||
          section.filterName === 'fleet' ||
          section.filterName === 'variant'
            ? []
            : section.options,
      }))
    );

    this.fleetField?.disable();
    this.variantField?.disable();
  }

  private getResolvedFilterValues(): Record<string, any> {
    const filters = this.filterForm.getRawValue();

    // When monthly calendar is active, compute the start/end of the selected
    // month and inject monthlyStartDate / monthlyEndDate so feature components
    // (e.g. Monthly Summary Report) receive consistent date-range fields.
    if (this.filterConfig.monthlyCalender) {
      const range: MonthRange =
        filters['monthlyCalender'] ?? this.getDefaultMonthRange();
      const { start } = this.universalFilterHelperService.getSelectedMonthRange(
        moment(range.start)
      );
      const { end } = this.universalFilterHelperService.getSelectedMonthRange(
        moment(range.end)
      );
      return {
        ...filters,
        monthlyCalender: {
          start: moment(range.start).format('YYYY-MM-DD HH:mm:ss'),
          end: moment(range.end).format('YYYY-MM-DD HH:mm:ss'),
        },
        monthlyStartDate: start,
        monthlyEndDate: end,
      };
    }

    // When the date filter is intentionally disabled for this component,
    // auto-inject a read-only "Today" range so the API always receives a
    // meaningful date without exposing the picker to users.
    if (!this.filterConfig.date) {
      const todayStart = moment().format('YYYY-MM-DD 00:00:00');
      const todayEnd = moment().format('YYYY-MM-DD 23:59:59');
      return {
        ...filters,
        date: {
          code: 'Today',
          name: 'Today',
          id: 'Today',
          default: true,
          startDate: todayStart,
          endDate: todayEnd,
        },
      };
    }

    if (!filters.date?.id) {
      return filters;
    }

    // const { startDate, endDate } = this.universalFilterHelperService.getDateRange(
    //   filters.date.id,
    //   this.selectedDate,
    //   // Pass the stored option as existingRange: when date === custom1 and
    //   // selectedDate is [] (navigation-back), the stored startDate/endDate
    //   // are used directly instead of moment(undefined) = current time.
    //   filters.date
    // );

    const selectedDateFilter = this.filterForm.get('date')?.value;
    const { startDate, endDate } =
      this.universalFilterHelperService.getDateRange(
        selectedDateFilter?.id,
        this.selectedDate,
        {
          startDate: selectedDateFilter?.startDate,
          endDate: selectedDateFilter?.endDate,
        }
      );

    const resolvedFilters = {
      ...filters,
      date: {
        ...filters.date,
        startDate,
        endDate,
      },
    };

    this.filterForm.get('date')?.setValue(resolvedFilters.date, {
      emitEvent: false,
    });

    return resolvedFilters;
  }

  private syncAppliedFilters(): Record<string, any> {
    const resolvedFilters = this.getResolvedFilterValues();
    return resolvedFilters;
  }

  private persistAndEmitFilters(): void {
    const resolvedFilters = this.syncAppliedFilters();
    this.store.dispatch(
      UniversalFilterActions.setUniversalFilter({ filters: resolvedFilters })
    );
    this.filterApply.emit(resolvedFilters);
  }

  /**
   * Handle individual filter change
   */
  onFilterChange(filterName: string, value: any): void {
    if (filterName === 'region') {
      // A user-driven change makes the region theirs, no longer auto-picked.
      this.autoPreselectedRegion = false;
    }
    this.selectedFilters[filterName] =
      this.payloadTransformService.extractCodes(value);
    if (this.selectedFilters['state'].length == 0 && filterName == 'state') {
      this.selectedFilters.state = '';
      this.selectedFilters.city = '';
    }

    if (
      this.selectedFilters['customer'].length == 0 &&
      filterName == 'customer'
    ) {
      this.selectedFilters.customer = '';
      this.selectedFilters.fleet = '';
    }

    if (this.selectedFilters['model']?.length == 0 && filterName == 'model') {
      this.selectedFilters.model = '';
      this.selectedFilters.variant = '';
    }

    this.fetchSelectionCombinedData(this.selectedFilters, filterName);
    const allEmpty = Object.values(this.selectedFilters).every(
      (value) => value === ''
    );

    if (allEmpty) {
      this.fetchCombinedData();
    }

    // Auto-apply for header-bar filters (customer, vehicle, date — those with no group).
    // Overlay-panel filters (region, state, city, dealer, fleet, model, variant)
    // require the explicit Apply button.
    const section = this.getFilterSection(filterName);
    if (!section?.group) {
      // Header-bar dropdowns (customer, vehicle) defer emitting until the
      // dropdown closes — handled by onHeaderDropdownClose().
      // Date is a single-select p-dropdown that auto-applies via getdateselection().
    }
  }

  /**
   * Called when a header-bar dropdown (no group) closes.
   * Resets search/pagination state and emits the applied filter values
   * so the API is only called once per user interaction rather than on
   * every individual item selection inside a multi-select dropdown.
   */
  onHeaderDropdownClose(methodNameEnding: string, filterName: string): void {
    this.onClear(methodNameEnding, filterName);
  }

  /**
   * Called when a single chip is removed via its × button in the multi-label
   * template. Removing one item from a multi-select does not auto-apply —
   * the user must click Apply, same as adding items. Only clearing the whole
   * selection via the group "clear all" control auto-applies (see
   * onHeaderFilterClear / ADO 35307).
   */
  onChipRemove(filterName: string): void {
    // No auto-emit; user must click Apply.
  }

  /**
   * Called when the user clears an entire selection via ng-select's built-in
   * "clear all" (×) control — for both header-bar filters and overlay-panel
   * (grouped) filters. Auto-applies immediately so the table refreshes
   * without requiring the user to click Apply (ADO 35307), matching the
   * "Clear Selection" button's existing auto-apply behavior. Only removing a
   * single chip (onChipRemove) still requires the explicit Apply button.
   */
  onHeaderFilterClear(methodNameEnding: string, filterName: string): void {
    this.onClear(methodNameEnding, filterName);
    this.persistAndEmitFilters();
    this.updateOverlayFilterIndicator();
  }
  hasSelectedFilters(): boolean {
    return Object.values(this.selectedFilters).some(
      (value) => value !== null && value !== undefined && value !== ''
    );
  }

  fetchSelectionCombinedData(selectedFilters: any, filterKey: any) {
    const filters = ['RGN', 'STT', 'DLR', 'CUST', 'VHL', 'MDL'];
    if (this.hasSelectedFilters()) {
      filters.push('CTY');
      this.updateFilterSection('city', { isLoading: true });
    } else {
      this.updateFilterSection('city', { options: [] });
      this.filterForm.controls['city'].setValue(null);
    }
    this.updateFilterSection('region', { isLoading: true });
    this.updateFilterSection('state', { isLoading: true });
    this.updateFilterSection('dealer', { isLoading: true });
    this.updateFilterSection('customer', { isLoading: true });
    this.updateFilterSection('vehicle', { isLoading: true });
    this.updateFilterSection('model', { isLoading: true });

    const requests = filters
      .filter(
        (filterType) => filterType !== this.getFilterTypeForKey(filterKey)
      )
      .map((filterType) => {
        const queryParams: any = {
          vehicleTypeCode: this.selectedVehicleType?.code || '',
          pageSize: this.pageSize,
          pageNumber: this.pageNumber,
          filterType: filterType,
          regionCode: selectedFilters.region || '',
          stateCode: selectedFilters.state || '',
          cityCode: selectedFilters.city || '',
          dealerCode: selectedFilters.dealer || '',
          customerCode: selectedFilters.customer || '',
          fleetCode: selectedFilters.fleet || '',
          vehicleCode: selectedFilters.vehicle || '',
          modelCode: this.selectedFilters?.model || '',
          variantCode: this.selectedFilters?.variant || '',
        };

        return this.reportService.getFilterData(queryParams);
      });

    if (requests.length === 0) {
      console.warn('No API requests to make.');
      return;
    }

    combineLatest(requests).subscribe({
      next: (responses) => {
        responses.forEach((response, index) => {
          const filterType = filters.filter(
            (type) => type !== this.getFilterTypeForKey(filterKey)
          )[index];

          const mappedKey = this.getKeyForFilterType(filterType);

          this.updateFilterData(mappedKey, response?.data?.response);
        });
        this.updateFilterSection('region', { isLoading: false });
        this.updateFilterSection('state', { isLoading: false });
        this.updateFilterSection('dealer', { isLoading: false });
        this.updateFilterSection('customer', { isLoading: false });
        this.updateFilterSection('vehicle', { isLoading: false });
        this.updateFilterSection('model', { isLoading: false });
        this.updateFilterSection('city', { isLoading: false });
      },
    });
  }

  getFilterTypeForKey(filterKey: string): string {
    // Mapping maintained in UniversalFilterUtilityService.
    return this.universalFilterUtility.getFilterTypeForKey(filterKey);
  }

  updateFilterData(key: string, data: any[] = []) {
    if (key && this.getFilterSection(key)?.options !== undefined) {
      this.updateFilterSection(key, { options: data || [] });
    } else {
      console.warn(`Key "${key}" not found on component.`);
    }
  }

  getKeyForFilterType(filterType: string): string {
    // Mapping maintained in UniversalFilterUtilityService.
    return this.universalFilterUtility.getKeyForFilterType(filterType);
  }

  onSelectChange(filterName: string, value: any): void {
    if (filterName === 'state') {
      const stateCodes = value.map((state: { code: any }) => state.code);
      if (stateCodes.length !== 0) {
        this.getCity(stateCodes);
      } else if (stateCodes.length == 0) {
        this.updateFilterSection('city', { options: [] });
        this.filterForm.controls['city'].setValue(null);
      }
    }
    if (filterName === 'customer') {
      const customerCode = value.map(
        (customer: { code: any }) => customer.code
      );
      if (customerCode.length !== 0) {
        this.fleetField?.enable();
        this.getFleet(customerCode);
      } else {
        // Customer cleared — reset fleet
        this.fleetField?.disable();
        this.fleetField?.setValue(null);
        this.updateFilterSection('fleet', { options: [] });
        this.updateOverlayFilterIndicator();
      }
    }

    if (filterName === 'vehicle') {
      this.selectedFilters.vehicle =
        this.payloadTransformService.extractCodes(value);
      this.refreshFleetForVehicleSelection();
    }

    if (filterName === 'model') {
      const modelCode = value.map((model: { code: any }) => model.code);

      if (modelCode.length !== 0) {
        this.variantField?.enable();
        this.getVariant(modelCode);
      } else {
        // Model cleared — reset variant
        this.variantField?.disable();
        this.variantField?.setValue(null);
        this.updateFilterSection('variant', { options: [] });
      }
    }

    if (filterName === 'application') {
      const applicationCodes = value.map((app: { code: any }) => app.code);
      this.selectedFilters.application =
        applicationCodes.length !== 0 ? applicationCodes.join(',') : '';
      this.loadDemandGenerator('');
    }
  }

  private refreshFleetForVehicleSelection(): void {
    const customerCodes = this.getCustomerCodesForFleetRefresh();

    if (
      !this.isCustomerView &&
      (!Array.isArray(customerCodes) || customerCodes.length === 0)
    ) {
      return;
    }

    this.getFleet(customerCodes);
  }

  private getCustomerCodesForFleetRefresh(): string | string[] {
    if (this.isCustomerView) {
      return '';
    }

    const customers = this.customerField?.value;

    if (!Array.isArray(customers) || customers.length === 0) {
      return '';
    }

    return customers.map((customer: { code: string }) => customer.code);
  }

  /**
   * Handle individual filter clear
   */
  onFilterClear(filterName: string): void {
    this.getFormControl(filterName).reset();
    this.filterChange.emit({ filterName, value: null });
  }

  /**
   * Toggle the advanced filter overlay panel
   */
  toggleAdvancedFilter(event: Event): void {
    this.advancedPanel.toggle(event);
    this.isAdvancedOpen = !this.isAdvancedOpen;
  }

  /**
   * When the advanced panel hides
   */
  onAdvancedPanelHide(): void {
    this.isAdvancedOpen = false;
  }

  /**
   * Called when the user selects a month in the monthly calendar picker.
   * Immediately persists the resolved date range to the store and emits to
   * the host feature component so the API is re-triggered without needing
   * the user to press an explicit "Apply" button.
   */
  onMonthlyCalenderChange(): void {
    // No auto-emit; user must click Apply.
  }

  /**
   * Apply all filters — emit the form values and persist to store
   */
  /**
   * Apply header-bar filters. Called when user clicks the Apply button
   * in the header filter bar.
   */
  applyHeaderFilters(): void {
    this.persistAndEmitFilters();
  }

  applyFilters(): void {
    this.persistAndEmitFilters();
    this.advancedPanel?.hide();
    this.updateOverlayFilterIndicator();
  }

  private updateOverlayFilterIndicator(): void {
    // Check if any overlay (grouped) filter has a value
    const overlayFilterNames = this.filterSections()
      .filter(
        (s) =>
          s.group === 'location' ||
          s.group === 'vehicle' ||
          s.group === 'sourceSpec'
      )
      .map((s) => s.filterName);

    const hasOverlay = overlayFilterNames.some((name) => {
      const val = this.filterForm.get(name)?.value;

      if (Array.isArray(val)) {
        return val.length > 0;
      }

      return val !== null && val !== undefined && val !== '';
    });

    this.hasOverlayFiltersApplied.set(hasOverlay);
  }

  /**
   * Clear all filters, reset the form, and clear store
   */
  clearAllFilters(): void {
    this.filterForm.reset();
    this.resetSelectedFilterState();
    // Reset date state
    this.customDate = false;
    this.selectedDate = [];
    this.defaultDate = [null, null];
    this.diff = 0;
    this.showTimePicker = false;
    this.resetFilterDateOptions();
    if (this.filterConfig?.date) {
      this.filterForm.get('date')?.setValue(this.getDefaultDate());
    } else if (!this.filterConfig?.monthlyCalender) {
      this.filterForm.get('date')?.setValue(this.todayOnlyOptions[0]);
    }

    if (this.filterConfig?.monthlyCalender) {
      this.filterForm
        .get('monthlyCalender')
        ?.setValue(this.getDefaultMonthRange());
    }

    if (!this.isCustomerOrDealerView) {
      // No Region preselect here: Clear Selection leaves Region empty (all
      // regions). The default region is only applied on the initial load.
      this.fetchCombinedData();
      // The cleared state is final: stop pages waiting for a preselect, and
      // the region is no longer an auto-picked one.
      this.universalFilterUtility.releaseRegionPreselect();
      this.autoPreselectedRegion = false;
    } else if (this.isCustomerView) {
      // Customer view: re-enable fleet and reload vehicle/model/fleet data after clear.
      this.fleetField?.enable();
      this.loadFleet('');
      this.loadVehicle('');
      this.loadModel('');
    } else {
      // Dealer view: reload customer + vehicle/model; fleet stays disabled until customer selected.
      this.loadCustomer('');
      this.loadVehicle('');
      this.loadModel('');
    }

    const clearedFilters = this.getResolvedFilterValues();
    this.store.dispatch(
      UniversalFilterActions.setUniversalFilter({ filters: clearedFilters })
    );
    this.filterApply.emit(clearedFilters);
    this.filterClear.emit();
    this.hasOverlayFiltersApplied.set(false);
    this.advancedPanel?.hide();

    // Reload Source and Specifications dropdowns after clear.
    if (this.filterConfig.application) {
      this.loadApplication('');
    }
    if (this.filterConfig.demandGenerator) {
      this.loadDemandGenerator('');
    }
    if (this.filterConfig.batteryCapacity) {
      this.loadBatteryCapacity('');
    }
  }

  onClear(methodNameEnding: string, fieldName: string) {
    if (fieldName === 'city') {
      return;
    }
    // Reused this method for clear
    // Region, State, City, Dealer, Customer, CustomerSingle, Fleet, Vehicle, VehicleSingle,
    const loadMethodKey = `load${methodNameEnding}`;
    this.updateFilterSection(fieldName, {
      pagination: { pageNumber: 1, totalRecords: 0, pageSize: 50 },
      searchTerm: '',
    });
    const searchTerm = this.getFilterSection(fieldName)?.searchTerm || '';
    this[loadMethodKey](searchTerm); //EX. loadCustomer(this.customerSearchTerm);
  }

  tooltipArray(items: any) {
    const tooltip = items
      .slice(1)
      .map((ele: any, i: number) => ele.name)
      .join(', ');
    return tooltip;
  }

  getFilterSection(filterName: string): FilterSection | undefined {
    return this.filterSections().find(
      (section) => section.filterName === filterName
    );
  }

  updateFilterSection(filterName: string, changes: Partial<FilterSection>) {
    this.filterSections.update((sections) =>
      sections.map((section) =>
        section.filterName === filterName ? { ...section, ...changes } : section
      )
    );
  }

  incrementPage(filterName: string): number {
    const section = this.getFilterSection(filterName);

    if (!section) {
      return 1;
    }

    const nextPage = section.pagination.pageNumber + 1;

    this.updateFilterSection(filterName, {
      pagination: {
        ...section.pagination,
        pageNumber: nextPage,
      },
    });

    return nextPage;
  }

  customSearchFn(term: string) {
    return term.trim();
  }

  onTyping(event: any, methodNameEnding: string, fieldName: string) {
    const loadMethodKey = `load${methodNameEnding}`;

    this.updateFilterSection(fieldName, {
      pagination: { pageNumber: 1, totalRecords: 0, pageSize: 50 },
      searchTerm: event.term,
    });

    if (event.term.length === 0 || event.term.length >= 2) {
      this[loadMethodKey](event.term);
    }
  }

  onScrollToEnd(methodNameEnding: string) {
    const fetchMoreKey = `fetchMore${methodNameEnding}`;
    this[fetchMoreKey]();
  }

  /**
   * Selects "West" in the Region filter, falling back to the first option when
   * West is unavailable, then applies it. No-op if the config flag is off or the
   * user already has a region selected (e.g. restored from the store).
   *
   * Pages using preselectRegion hold their first API calls until this emits, so
   * when there is nothing to select (empty list / failed request) the current
   * state is still emitted to release them.
   */
  private preselectDefaultRegion(regions: any[] = []): boolean {
    const regionField = this.filterForm?.get('region');
    if (
      !this.filterConfig.preselectRegion ||
      !regionField ||
      regionField.value?.length > 0 ||
      // Returning to the page with other filters (customer, vehicle, ...)
      // already applied: leave them as the user set them.
      this.universalFilterUtility.hasOtherFiltersSelected(
        this.filterForm.getRawValue()
      )
    ) {
      this.universalFilterUtility.releaseRegionPreselect();
      return false;
    }

    if (!regions?.length) {
      this.universalFilterUtility.releaseRegionPreselect();
      this.persistAndEmitFilters();
      return false;
    }

    const defaultRegion =
      regions.find((r) => r?.name?.trim().toLowerCase() === 'west') ??
      regions[0];

    regionField.setValue([defaultRegion], { emitEvent: false });
    this.onFilterChange('region', [defaultRegion]);
    this.autoPreselectedRegion = true;
    // Release before emitting so the page applies this state instead of holding it.
    this.universalFilterUtility.releaseRegionPreselect();
    this.persistAndEmitFilters();
    this.updateOverlayFilterIndicator();
    return true;
  }

  /**
   * True when preselectDefaultRegion would pick a region right now: the page
   * asked for it and the form holds neither a region nor any other selection.
   * Mirrors that method's own guard clauses.
   */
  private willPreselectRegion(): boolean {
    const regionField = this.filterForm?.get('region');
    return (
      !!this.filterConfig.preselectRegion &&
      !!regionField &&
      !(regionField.value?.length > 0) &&
      !this.universalFilterUtility.hasOtherFiltersSelected(
        this.filterForm.getRawValue()
      )
    );
  }

  fetchCombinedData(preselectRegion = false) {
    // Active filter types are derived from filterConfig via UniversalFilterUtilityService,
    // matching the OEM filter's pattern of boolean-config → API-type mapping.
    const allFilters = this.universalFilterUtility.getActiveFilterTypes(
      this.filterConfig
    );

    // When a Region is about to be preselected, every other dropdown is reloaded
    // scoped to that region right after (fetchSelectionCombinedData), so loading
    // them unfiltered first only duplicates those calls. Fetch just the region
    // list; the rest is loaded in full if no region ends up being selected.
    const preselecting =
      preselectRegion &&
      allFilters.includes('RGN') &&
      this.willPreselectRegion();
    const activeFilters = preselecting ? ['RGN'] : allFilters;

    const requests = activeFilters.map((filterType) =>
      this.reportService.getFilterData(this.createQueryParams(filterType))
    );

    // A newer pass supersedes any in-flight one, so its stale responses (and
    // duplicate requests) are dropped instead of racing the preselect.
    this.combinedDataSub?.unsubscribe();
    this.combinedDataSub = combineLatest(requests)
      .pipe(
        takeUntil(this.unsubscribe$),
        catchError(() => {
          if (preselectRegion) {
            this.preselectDefaultRegion([]);
            if (preselecting) {
              this.fetchCombinedData();
            }
          }
          return EMPTY;
        })
      )
      .subscribe((responses) => {
        let responseIndex = 0;

        if (activeFilters.includes('RGN')) {
          const regions = responses[responseIndex++].data.response;
          this.updateFilterSection('region', { options: regions });
          if (preselectRegion) {
            const preselected = this.preselectDefaultRegion(regions);
            if (preselecting && !preselected) {
              this.fetchCombinedData();
            }
          }
        }
        if (activeFilters.includes('STT')) {
          this.updateFilterSection('state', {
            options: responses[responseIndex++].data.response,
          });
        }
        if (activeFilters.includes('DLR')) {
          this.updateFilterSection('dealer', {
            options: responses[responseIndex++].data.response,
          });
        }
        if (activeFilters.includes('CUST')) {
          this.updateFilterSection('customer', {
            options: responses[responseIndex++].data.response,
          });
        }
        if (activeFilters.includes('VHL')) {
          const vehicleResponse = responses[responseIndex++].data.response;
          this.updateFilterSection('vehicle', {
            options: vehicleResponse,
          });
        }
        if (activeFilters.includes('MDL')) {
          const modelResponse = responses[responseIndex++].data.response;
          this.updateFilterSection('model', {
            options: modelResponse,
          });
        }
      });

    if (this.filterForm.value?.state) {
      this.getCity(this.filterForm.value?.state);
    }
  }

  private createQueryParams(filterType: string) {
    // Query-param structure is centralised in UniversalFilterUtilityService
    // so every API call follows the same OEM-filter pattern.
    return this.universalFilterUtility.buildQueryParams(
      filterType,
      this.commonService?.getVehicleTypeCode(),
      this.pageSize,
      this.pageNumber,
      this.selectedFilters
    );
  }

  loadCustomer(searchTerm?: string, changingVehicleType?: any) {
    this.updateFilterSection('customer', { isLoading: true });
    const queryParams = {
      ...(searchTerm && { keyword: searchTerm ?? '' }),
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('customer')?.pagination.pageSize,
      pageNumber: this.getFilterSection('customer')?.pagination.pageNumber,
      filterType: 'CUST',
      regionCode: this.selectedFilters?.region || '',
      stateCode: this.selectedFilters?.state || '',
      cityCode: this.selectedFilters?.city || '',
      dealerCode: this.selectedFilters?.dealer || '',
      fleetCode: this.selectedFilters?.fleet || '',
      vehicleCode: this.selectedFilters?.vehicle || '',
      modelCode: this.selectedFilters?.model || '',
      variantCode: this.selectedFilters?.variant || '',
    };
    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('customer', {
              options: resp.data.response,
            });
          }
          this.updateFilterSection('customer', { isLoading: false });
        },
        error: (err) => {
          this.updateFilterSection('customer', { isLoading: false });
          this.updateFilterSection('customer', { options: [] });
        },
      });
  }

  onScrollCustomer(end: any) {
    const customerOptions = this.getFilterSection('customer')?.options || [];

    if (
      this.getFilterSection('customer')?.isLoading ||
      this.customer.length <= customerOptions.length
    ) {
      return;
    }

    if (
      end + this.numberOfItemsFromEndBeforeFetchingMore >=
      customerOptions.length
    ) {
      this.fetchMoreCustomer();
    }
  }

  fetchMoreCustomer() {
    let queryParams;
    this.updateFilterSection('customer', { isLoading: true });

    if (
      this.getFilterSection('customer')!.searchTerm.length <= 0 ||
      this.getFilterSection('customer')?.searchTerm == undefined
    ) {
      this.incrementPage('customer');
      queryParams = {
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('customer')?.pagination.pageSize,
        pageNumber: this.getFilterSection('customer')?.pagination.pageNumber,
        filterType: 'CUST',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    } else {
      this.incrementPage('customer');

      queryParams = {
        keyword: this.getFilterSection('customer')!.searchTerm,
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('customer')?.pagination.pageSize,
        pageNumber: this.getFilterSection('customer')?.pagination.pageNumber,
        filterType: 'CUST',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    }

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            const options = this.getFilterSection('customer')?.options || [];
            this.updateFilterSection('customer', {
              isLoading: false,
              options: [...options, ...resp.data.response],
            });
          }
        },
        error: (err) => {
          this.updateFilterSection('customer', { isLoading: false });
        },
      });
  }

  loadVehicle(searchTerm?: string) {
    this.updateFilterSection('vehicle', { options: [], isLoading: true });
    const queryParams = {
      keyword: searchTerm || '',
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('vehicle')?.pagination.pageSize,
      pageNumber: this.getFilterSection('vehicle')?.pagination.pageNumber,
      filterType: 'VHL',
      regionCode: this.selectedFilters?.region || '',
      stateCode: this.selectedFilters?.state || '',
      cityCode: this.selectedFilters?.city || '',
      dealerCode: this.selectedFilters?.dealer || '',
      customerCode: this.selectedFilters?.customer || '',
      fleetCode: this.selectedFilters?.fleet || '',
      modelCode: this.selectedFilters?.model || '',
      variantCode: this.selectedFilters?.variant || '',
    };
    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('vehicle', {
              isLoading: false,
              options: resp.data.response,
            });
          }
        },
        error: (err) => {
          this.updateFilterSection('vehicle', {
            isLoading: false,
            options: [],
          });
        },
      });
  }

  onScrollVechicle(end: any) {
    const vehicleLoading = this.getFilterSection('vehicle')?.isLoading;
    const vehicleOptions = this.getFilterSection('vehicle')?.options || [];
    if (vehicleLoading || this.vehicle.length <= vehicleOptions.length) {
      return;
    }

    if (
      end + this.numberOfItemsFromEndBeforeFetchingMore >=
      vehicleOptions.length
    ) {
      this.fetchMoreVehicle();
    }
  }

  fetchMoreVehicle() {
    let queryParams;
    this.updateFilterSection('vehicle', { isLoading: true });

    if (
      this.getFilterSection('vehicle')!.searchTerm?.length <= 0 ||
      this.getFilterSection('vehicle')?.searchTerm == undefined
    ) {
      this.incrementPage('vehicle');

      queryParams = {
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('vehicle')?.pagination.pageSize,
        pageNumber: this.getFilterSection('vehicle')?.pagination.pageNumber,
        filterType: 'VHL',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    } else {
      this.incrementPage('vehicle');

      queryParams = {
        keyword: this.getFilterSection('vehicle')?.searchTerm,
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('vehicle')?.pagination.pageSize,
        pageNumber: this.getFilterSection('vehicle')?.pagination.pageNumber,
        filterType: 'VHL',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    }

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            const options = this.getFilterSection('vehicle')?.options || [];
            this.updateFilterSection('vehicle', {
              isLoading: false,
              options: [...options, ...resp.data.response],
            });
          }
          this.updateFilterSection('vehicle', {
            isLoading: false,
          });
        },
        error: (err) => {
          this.updateFilterSection('vehicle', {
            isLoading: false,
            options: [],
          });
        },
      });
  }

  loadRegion(searchTerm: string) {
    this.updateFilterSection('region', { options: [], isLoading: true });

    const queryParams = {
      keyword: searchTerm,
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('region')?.pagination.pageSize,
      pageNumber: this.getFilterSection('region')?.pagination.pageNumber,
      filterType: 'RGN',
      stateCode: this.selectedFilters?.state || '',
      cityCode: this.selectedFilters?.city || '',
      dealerCode: this.selectedFilters?.dealer || '',
      customerCode: this.selectedFilters?.customer || '',
      fleetCode: this.selectedFilters?.fleet || '',
      vehicleCode: this.selectedFilters?.vehicle || '',
      modelCode: this.selectedFilters?.model || '',
      variantCode: this.selectedFilters?.variant || '',
    };
    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('region', {
              options: resp.data.response,
            });
          }
          this.updateFilterSection('region', {
            isLoading: false,
          });
        },
        error: (err) => {
          this.updateFilterSection('region', {
            options: [],
            isLoading: false,
          });
        },
      });
  }

  onScrollRegion(end: any) {
    const filterSection = this.getFilterSection('region');
    if (
      filterSection?.isLoading ||
      this.regions.length <= filterSection!.options.length
    ) {
      return;
    }

    if (
      end + this.numberOfItemsFromEndBeforeFetchingMore >=
      filterSection!.options.length
    ) {
      this.fetchMoreRegion();
    }
  }

  fetchMoreRegion() {
    let queryParams;
    this.updateFilterSection('region', { isLoading: true });
    const filterSection = this.getFilterSection('region');
    if (
      filterSection!.searchTerm?.length <= 0 ||
      filterSection!.searchTerm == undefined
    ) {
      this.incrementPage('region');

      queryParams = {
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('region')?.pagination.pageSize,
        pageNumber: this.getFilterSection('region')?.pagination.pageNumber,
        filterType: 'RGN',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    } else {
      this.incrementPage('region');

      queryParams = {
        keyword: filterSection?.searchTerm || '',
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('region')?.pagination.pageSize,
        pageNumber: this.getFilterSection('region')?.pagination.pageNumber,
        filterType: 'RGN',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    }

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            const options = this.getFilterSection('region')?.options || [];
            this.updateFilterSection('region', {
              isLoading: false,
              options: [...options, ...resp.data.response],
            });
          }
          this.updateFilterSection('region', {
            isLoading: false,
          });
        },
        error: (err) => {
          this.updateFilterSection('region', {
            isLoading: false,
            options: [],
          });
        },
      });
  }

  loadDealer(searchTerm: string) {
    this.updateFilterSection('dealer', { options: [], isLoading: true });
    const queryParams = {
      keyword: searchTerm,
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('dealer')?.pagination.pageSize,
      pageNumber: this.getFilterSection('dealer')?.pagination.pageNumber,
      filterType: 'DLR',
      regionCode: this.selectedFilters?.region || '',
      stateCode: this.selectedFilters?.state || '',
      cityCode: this.selectedFilters?.city || '',
      customerCode: this.selectedFilters?.customer || '',
      fleetCode: this.selectedFilters?.fleet || '',
      vehicleCode: this.selectedFilters?.vehicle || '',
      modelCode: this.selectedFilters?.model || '',
      variantCode: this.selectedFilters?.variant || '',
    };
    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('dealer', {
              options: resp.data.response,
            });
          }
          this.updateFilterSection('dealer', {
            isLoading: false,
          });
        },
        error: (err) => {
          this.updateFilterSection('dealer', {
            options: [],
            isLoading: false,
          });
        },
      });
  }

  onScrollDealer(end: any) {
    const dealerLoading = this.getFilterSection('dealer')?.isLoading;
    const dealerOptions = this.getFilterSection('dealer')?.options || [];
    if (dealerLoading || this.dealers.length <= dealerOptions.length) {
      return;
    }

    if (
      end + this.numberOfItemsFromEndBeforeFetchingMore >=
      dealerOptions.length
    ) {
      this.fetchMoreDealer();
    }
  }

  fetchMoreDealer() {
    let queryParams;
    this.updateFilterSection('dealer', { isLoading: true });

    if (
      this.getFilterSection('dealer')!.searchTerm?.length <= 0 ||
      this.getFilterSection('dealer')!.searchTerm == undefined
    ) {
      this.incrementPage('dealer');

      queryParams = {
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('dealer')?.pagination.pageSize,
        pageNumber: this.getFilterSection('dealer')?.pagination.pageNumber,
        filterType: 'DLR',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    } else {
      this.incrementPage('dealer');

      queryParams = {
        keyword: this.getFilterSection('dealer')!.searchTerm,
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('dealer')?.pagination.pageSize,
        pageNumber: this.getFilterSection('dealer')?.pagination.pageNumber,
        filterType: 'DLR',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    }

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            const options = this.getFilterSection('dealer')?.options || [];
            this.updateFilterSection('dealer', {
              isLoading: false,
              options: [...options, ...resp.data.response],
            });
          }
          this.updateFilterSection('vehicle', {
            isLoading: false,
          });
        },
        error: (err) => {
          this.updateFilterSection('dealer', {
            isLoading: false,
            options: [],
          });
        },
      });
  }

  loadState(searchTerm: string) {
    this.updateFilterSection('state', { options: [], isLoading: true });
    const queryParams = {
      keyword: searchTerm,
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('state')?.pagination.pageSize,
      pageNumber: this.getFilterSection('state')?.pagination.pageNumber,
      filterType: 'STT',
      regionCode: this.selectedFilters?.region || '',
      cityCode: this.selectedFilters?.city || '',
      dealerCode: this.selectedFilters?.dealer || '',
      customerCode: this.selectedFilters?.customer || '',
      fleetCode: this.selectedFilters?.fleet || '',
      vehicleCode: this.selectedFilters?.vehicle || '',
      modelCode: this.selectedFilters?.model || '',
      variantCode: this.selectedFilters?.variant || '',
    };
    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('state', {
              options: resp.data.response,
            });
          }
          this.updateFilterSection('state', {
            isLoading: false,
          });
        },
        error: (err) => {
          this.updateFilterSection('state', {
            options: [],
            isLoading: false,
          });
        },
      });
  }

  onScrollState(end: any) {
    const stateLoading = this.getFilterSection('state')?.isLoading;
    const stateOptions = this.getFilterSection('state')?.options || [];
    if (stateLoading || this.states.length <= stateOptions.length) {
      return;
    }

    if (
      end + this.numberOfItemsFromEndBeforeFetchingMore >=
      stateOptions.length
    ) {
      this.fetchMoreState();
    }
  }

  fetchMoreState() {
    let queryParams;
    this.updateFilterSection('state', {
      isLoading: true,
    });

    if (
      this.getFilterSection('state')!.searchTerm?.length <= 0 ||
      this.getFilterSection('state')!.searchTerm == undefined
    ) {
      this.incrementPage('state');

      queryParams = {
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('state')?.pagination.pageSize,
        pageNumber: this.getFilterSection('state')?.pagination.pageNumber,
        filterType: 'STT',
        regionCode: this.selectedFilters?.region || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    } else {
      this.incrementPage('state');

      queryParams = {
        keyword: this.getFilterSection('state')?.searchTerm,
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('state')?.pagination.pageSize,
        pageNumber: this.getFilterSection('state')?.pagination.pageNumber,
        filterType: 'STT',
        regionCode: this.selectedFilters?.region || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    }

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            const options = this.getFilterSection('state')?.options || [];
            this.updateFilterSection('state', {
              options: [...options, ...resp.data.response],
            });
          }
          this.updateFilterSection('state', {
            isLoading: false,
          });
        },
        error: (err) => {
          this.updateFilterSection('state', {
            isLoading: false,
            options: [],
          });
        },
      });
  }

  getCity(stateCodes: string[]): void {
    this.updateFilterSection('city', { options: [], isLoading: true });
    const queryParams = {
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: 500,
      pageNumber: this.pageNumber,
      filterType: 'CTY',
      regionCode: this.selectedFilters?.region || '',
      stateCode: stateCodes.join(',') || '',
      dealerCode: this.selectedFilters?.dealer || '',
      customerCode: this.selectedFilters?.customer || '',
      fleetCode: this.selectedFilters?.fleet || '',
      vehicleCode: this.selectedFilters?.vehicle || '',
      modelCode: this.selectedFilters?.model || '',
      variantCode: this.selectedFilters?.variant || '',
    };

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (resp) => {
          if (resp?.status) {
            // this.updateFilterSection('city', {
            //   // options: resp.data.response,
            //   options: resp.data.response
            //     .filter(
            //       (item: any) => item?.stateName && item?.name && item?.code
            //     )
            //     .sort((a: any, b: any) => {
            //       const stateCompare = (a.stateName || '').localeCompare(
            //         b.stateName || ''
            //       );

            //       if (stateCompare !== 0) {
            //         return stateCompare;
            //       }

            //       return (a.name || '').localeCompare(b.name || '');
            //     }),
            // });
            const sortedCities = [...resp.data.response]
              .filter(
                (item: any) => item?.stateName && item?.name && item?.code
              )
              .sort((a: any, b: any) => a.stateName.localeCompare(b.stateName));

            this.updateFilterSection('city', {
              options: [],
            });

            setTimeout(() => {
              this.updateFilterSection('city', {
                options: sortedCities,
              });
            });
          }
          // this.updateFilterSection('city', { isLoading: false });
        },
        (err) => {
          this.updateFilterSection('city', { options: [], isLoading: false });
        }
      );
  }

  /**
   * Common handler for ng-select (open) events across all overlay filters.
   * Add per-field logic here as needed, gated by filterName.
   */
  onDropdownOpen(section: FilterSection): void {
    if (section.filterName === 'city') {
      const currentOptions = this.getFilterSection('city')?.options || [];

      const sortedCities = [...currentOptions]
        .filter((item: any) => item?.stateName && item?.name && item?.code)
        .sort((a: any, b: any) => {
          const stateCompare = (a.stateName || '')
            .trim()
            .localeCompare((b.stateName || '').trim());
          if (stateCompare !== 0) {
            return stateCompare;
          }
          return (a.name || '').trim().localeCompare((b.name || '').trim());
        });

      setTimeout(() => {
        this.updateFilterSection('city', { options: sortedCities });
      }, 0);
    }
  }

  loadCity(searchTerm: string) {
    this.updateFilterSection('city', { options: [], isLoading: true });
    const queryParams = {
      keyword: searchTerm,
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('city')?.pagination.pageSize,
      pageNumber: this.getFilterSection('city')?.pagination.pageNumber,
      filterType: 'CTY',
      regionCode: this.selectedFilters?.region || '',
      stateCode: this.selectedFilters?.state || '',
      dealerCode: this.selectedFilters?.dealer || '',
      customerCode: this.selectedFilters?.customer || '',
      fleetCode: this.selectedFilters?.fleet || '',
      vehicleCode: this.selectedFilters?.vehicle || '',
      modelCode: this.selectedFilters?.model || '',
      variantCode: this.selectedFilters?.variant || '',
    };
    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('city', {
              options: resp.data.response,
            });
          }
          this.updateFilterSection('city', { isLoading: false });
        },
        error: (err) => {
          this.updateFilterSection('city', { options: [], isLoading: false });
        },
      });
  }

  onScrollCity(end: any) {
    const cityLoading = this.getFilterSection('city')?.isLoading;
    const cityOptions = this.getFilterSection('city')?.options || [];
    if (cityLoading || this.citys.length <= cityOptions.length) {
      return;
    }

    if (
      end + this.numberOfItemsFromEndBeforeFetchingMore >=
      cityOptions.length
    ) {
      this.fetchMoreCity();
    }
  }

  fetchMoreCity() {
    const selectState = this.filterForm.controls['state'].value;
    const stateCodes = selectState.map((state: { code: any }) => state.code);
    let queryParams;
    this.updateFilterSection('city', { isLoading: true });

    if (
      this.getFilterSection('city')!.searchTerm?.length <= 0 ||
      this.getFilterSection('city')!.searchTerm == undefined
    ) {
      this.incrementPage('city');
      queryParams = {
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('city')?.pagination.pageSize,
        pageNumber: this.getFilterSection('city')?.pagination.pageNumber,
        filterType: 'CTY',
        stateCode: stateCodes.join(',') || '',
        regionCode: this.selectedFilters?.region || '',
        dealerCode: this.selectedFilters?.dealer || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    } else {
      this.incrementPage('city');

      queryParams = {
        keyword: this.getFilterSection('city')?.searchTerm,
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('city')?.pagination.pageSize,
        pageNumber: this.getFilterSection('city')?.pagination.pageNumber,
        filterType: 'CTY',
        stateCode: stateCodes.join(',') || '',
        regionCode: this.selectedFilters?.region || '',
        dealerCode: this.selectedFilters?.dealer || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    }

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            const options = this.getFilterSection('city')?.options || [];
            this.updateFilterSection('city', {
              options: [...options, ...resp.data.response],
            });
          }
          this.updateFilterSection('city', { isLoading: false });
        },
        error: (err) => {
          this.updateFilterSection('city', { options: [], isLoading: false });
        },
      });
  }

  getFleet(customerCode: string | string[] = ''): void {
    const queryParams = {
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.pageSize,
      pageNumber: this.pageNumber,
      filterType: 'FLT',
      regionCode: this.selectedFilters?.region || '',
      stateCode: this.selectedFilters?.state || '',
      dealerCode: this.selectedFilters?.dealer || '',
      customerCode: Array.isArray(customerCode)
        ? customerCode.join(',')
        : customerCode || '',
      fleetCode: this.selectedFilters?.fleet || '',
      vehicleCode: this.selectedFilters?.vehicle || '',
      modelCode: this.selectedFilters?.model || '',
      variantCode: this.selectedFilters?.variant || '',
    };

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('fleet', {
              options: resp.data.response,
            });
          }
        },
        error: (err) => {
          this.updateFilterSection('fleet', { options: [], isLoading: false });
        },
      });
  }

  get customerField() {
    return this.filterForm.get('customer') as FormControl;
  }

  get fleetField() {
    return this.filterForm.get('fleet') as FormControl;
  }

  get modelField() {
    return this.filterForm.get('model') as FormControl;
  }

  get variantField() {
    return this.filterForm.get('variant') as FormControl;
  }

  loadFleet(searchTerm: string) {
    this.updateFilterSection('fleet', { isLoading: true });
    const queryParams = {
      keyword: searchTerm,
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('fleet')?.pagination.pageSize,
      pageNumber: this.getFilterSection('fleet')?.pagination.pageNumber,
      filterType: 'FLT',
      regionCode: this.selectedFilters?.region || '',
      stateCode: this.selectedFilters?.state || '',
      cityCode: this.selectedFilters?.city || '',
      dealerCode: this.selectedFilters?.dealer || '',
      customerCode: this.selectedFilters?.customer || '',
      vehicleCode: this.selectedFilters?.vehicle || '',
      modelCode: this.selectedFilters?.model || '',
      variantCode: this.selectedFilters?.variant || '',
    };
    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('fleet', {
              options: resp.data.response,
            });
          }
          this.updateFilterSection('fleet', { isLoading: false });
        },
        error: (err) => {
          this.updateFilterSection('fleet', { isLoading: false, options: [] });
        },
      });
  }

  onScrollFleet(end: any) {
    const fleetLoading = this.getFilterSection('fleet')?.isLoading;
    const fleetOptions = this.getFilterSection('fleet')?.options || [];
    if (fleetLoading || this.fleets.length <= fleetOptions.length) {
      return;
    }

    if (
      end + this.numberOfItemsFromEndBeforeFetchingMore >=
      fleetOptions.length
    ) {
      this.fetchMoreFleet();
    }
  }

  fetchMoreFleet() {
    const selectCustomer = this.filterForm.controls['customer'].value;
    const customerCode = selectCustomer.map(
      (customer: { code: any }) => customer.code
    );
    let queryParams;
    this.updateFilterSection('fleet', { isLoading: true });

    if (
      this.getFilterSection('vehicle')!.searchTerm?.length <= 0 ||
      this.getFilterSection('vehicle')!.searchTerm == undefined
    ) {
      this.incrementPage('fleet');

      queryParams = {
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('fleet')?.pagination.pageSize,
        pageNumber: this.getFilterSection('fleet')?.pagination.pageNumber,
        filterType: 'FLT',
        customerCode: customerCode?.join(',') || '',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    } else {
      this.incrementPage('fleet');

      queryParams = {
        keyword: this.getFilterSection('vehicle')!.searchTerm,
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('fleet')?.pagination.pageSize,
        pageNumber: this.getFilterSection('fleet')?.pagination.pageNumber,
        filterType: 'FLT',
        customerCode: customerCode?.join(',') || '',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: this.selectedFilters?.model || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    }

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            const options = this.getFilterSection('fleet')?.options || [];
            this.updateFilterSection('fleet', {
              options: [...options, ...resp.data.response],
            });
          }
          this.updateFilterSection('fleet', { isLoading: false });
        },
        error: (err) => {
          this.updateFilterSection('fleet', { options: [], isLoading: false });
        },
      });
  }

  loadModel(searchTerm?: string, changingVehicleType?: any) {
    this.updateFilterSection('model', { options: [], isLoading: true });
    const queryParams = {
      ...(searchTerm && { keyword: searchTerm ?? '' }),
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('model')?.pagination.pageSize,
      pageNumber: this.getFilterSection('model')?.pagination.pageNumber,
      filterType: 'MDL',
      regionCode: this.selectedFilters?.region || '',
      stateCode: this.selectedFilters?.state || '',
      cityCode: this.selectedFilters?.city || '',
      dealerCode: this.selectedFilters?.dealer || '',
      customerCode: this.selectedFilters?.customer || '',
      fleetCode: this.selectedFilters?.fleet || '',
      vehicleCode: this.selectedFilters?.vehicle || '',
      variantCode: this.selectedFilters?.variant || '',
    };
    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('model', {
              options: resp.data.response,
              isLoading: false,
            });
          } else {
            this.updateFilterSection('model', {
              options: [],
              isLoading: false,
            });
          }
        },
        error: (err) => {
          this.updateFilterSection('model', { options: [], isLoading: false });
        },
      });
  }

  onScrollModel(end: any) {
    const modelLoading = this.getFilterSection('model')?.isLoading;
    const modelOptions = this.getFilterSection('model')?.options || [];
    if (modelLoading || this.models.length <= modelOptions.length) {
      return;
    }

    if (
      end + this.numberOfItemsFromEndBeforeFetchingMore >=
      modelOptions.length
    ) {
      this.fetchMoreModel();
    }
  }

  fetchMoreModel() {
    let queryParams;
    this.updateFilterSection('model', { isLoading: true });

    if (
      this.getFilterSection('model')!.searchTerm?.length <= 0 ||
      this.getFilterSection('model')!.searchTerm == undefined
    ) {
      this.incrementPage('model');

      queryParams = {
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('model')?.pagination.pageSize,
        pageNumber: this.getFilterSection('model')?.pagination.pageNumber,
        filterType: 'MDL',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    } else {
      this.incrementPage('model');

      queryParams = {
        keyword: this.getFilterSection('model')?.searchTerm,
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('model')?.pagination.pageSize,
        pageNumber: this.getFilterSection('model')?.pagination.pageNumber,
        filterType: 'MDL',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        customerCode: this.selectedFilters?.customer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        variantCode: this.selectedFilters?.variant || '',
      };
    }

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            const options = this.getFilterSection('model')?.options || [];
            this.updateFilterSection('model', {
              options: [...options, ...resp.data.response],
            });
          }
          this.updateFilterSection('model', { isLoading: false });
        },
        error: (err) => {
          this.updateFilterSection('model', { options: [], isLoading: false });
        },
      });
  }

  getVariant(modelCode: string | string[] = ''): void {
    const queryParams = {
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.pageSize,
      pageNumber: this.pageNumber,
      filterType: 'VRT',
      regionCode: this.selectedFilters?.region || '',
      stateCode: this.selectedFilters?.state || '',
      dealerCode: this.selectedFilters?.dealer || '',
      customerCode: this.selectedFilters?.customer || '',
      fleetCode: this.selectedFilters?.fleet || '',
      vehicleCode: this.selectedFilters?.vehicle || '',
      modelCode: Array.isArray(modelCode)
        ? modelCode.join(',')
        : modelCode || '',
      variantCode: this.selectedFilters?.variant || '',
    };

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('variant', {
              options: resp.data.response,
            });
          }
        },
      });
  }

  loadVariant(searchTerm: string) {
    this.updateFilterSection('variant', { options: [], isLoading: true });
    const queryParams = {
      keyword: searchTerm,
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('variant')?.pagination.pageSize,
      pageNumber: this.getFilterSection('variant')?.pagination.pageNumber,
      filterType: 'VRT',
      regionCode: this.selectedFilters?.region || '',
      stateCode: this.selectedFilters?.state || '',
      cityCode: this.selectedFilters?.city || '',
      dealerCode: this.selectedFilters?.dealer || '',
      customerCode: this.selectedFilters?.customer || '',
      fleetCode: this.selectedFilters?.fleet || '',
      vehicleCode: this.selectedFilters?.vehicle || '',
      modelCode: this.selectedFilters?.model || '',
    };
    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('variant', {
              options: resp.data.response,
            });
          }
          this.updateFilterSection('variant', { isLoading: false });
        },
        error: (err) => {
          this.updateFilterSection('variant', {
            options: [],
            isLoading: false,
          });
        },
      });
  }

  onScrollVariant(end: any) {
    const variantLoading = this.getFilterSection('variant')?.isLoading;
    const variantOptions = this.getFilterSection('variant')?.options || [];
    if (variantLoading || this.variants.length <= variantOptions.length) {
      return;
    }

    if (
      end + this.numberOfItemsFromEndBeforeFetchingMore >=
      variantOptions.length
    ) {
      this.fetchMoreVariant();
    }
  }

  fetchMoreVariant() {
    const selectModel = this.filterForm.controls['model'].value;
    const modelCode = selectModel.map((model: { code: any }) => model.code);
    let queryParams;
    this.updateFilterSection('variant', { isLoading: true });

    if (
      this.getFilterSection('vehicle')!.searchTerm?.length <= 0 ||
      this.getFilterSection('vehicle')!.searchTerm == undefined
    ) {
      this.incrementPage('variant');

      queryParams = {
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('variant')?.pagination.pageSize,
        pageNumber: this.getFilterSection('variant')?.pagination.pageNumber,
        filterType: 'VRT',
        customerCode: this.selectedFilters?.customer || '',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: modelCode?.join(',') || '',
      };
    } else {
      this.incrementPage('variant');

      queryParams = {
        keyword: this.getFilterSection('vehicle')!.searchTerm,
        vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
        pageSize: this.getFilterSection('variant')?.pagination.pageSize,
        pageNumber: this.getFilterSection('variant')?.pagination.pageNumber,
        filterType: 'VRT',
        customerCode: this.selectedFilters?.customer || '',
        regionCode: this.selectedFilters?.region || '',
        stateCode: this.selectedFilters?.state || '',
        cityCode: this.selectedFilters?.city || '',
        dealerCode: this.selectedFilters?.dealer || '',
        fleetCode: this.selectedFilters?.fleet || '',
        vehicleCode: this.selectedFilters?.vehicle || '',
        modelCode: modelCode?.join(',') || '',
      };
    }

    this.reportService
      .getFilterData(queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          this.updateFilterSection('variant', { isLoading: false });
          if (resp?.status) {
            const options = this.getFilterSection('variant')?.options || [];
            this.updateFilterSection('variant', {
              isLoading: false,
              options: [...options, ...resp.data.response],
            });
          }
        },
        error: (err) => {
          this.updateFilterSection('variant', {
            options: [],
            isLoading: false,
          });
        },
      });
  }

  // ─── Application / Demand Generator / Battery Capacity ─────────────────

  get applicationField() {
    return this.filterForm.get('application') as FormControl;
  }

  get demandGeneratorField() {
    return this.filterForm.get('demandGenerator') as FormControl;
  }

  get batteryCapacityField() {
    return this.filterForm.get('batteryCapacity') as FormControl;
  }

  loadApplication(searchTerm: string) {
    this.updateFilterSection('application', { isLoading: true });
    const queryParams = {
      ...(searchTerm && { keyword: searchTerm }),
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('application')?.pagination.pageSize,
      pageNumber: this.getFilterSection('application')?.pagination.pageNumber,
    };
    this.ssoService
      .getSharedURL(FILTER_APPLICATION, queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('application', {
              options: resp.data.response,
              isLoading: false,
            });
          } else {
            this.updateFilterSection('application', { isLoading: false });
          }
        },
        error: () => {
          this.updateFilterSection('application', {
            options: [],
            isLoading: false,
          });
        },
      });
  }

  fetchMoreApplication() {
    this.updateFilterSection('application', { isLoading: true });
    const section = this.getFilterSection('application');
    this.incrementPage('application');

    const queryParams: any = {
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: section?.pagination.pageSize,
      pageNumber: this.getFilterSection('application')?.pagination.pageNumber,
    };
    if (section?.searchTerm) {
      queryParams.keyword = section.searchTerm;
    }

    this.ssoService
      .getSharedURL(FILTER_APPLICATION, queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            const existing =
              this.getFilterSection('application')?.options || [];
            this.updateFilterSection('application', {
              options: [...existing, ...resp.data.response],
              isLoading: false,
            });
          } else {
            this.updateFilterSection('application', { isLoading: false });
          }
        },
        error: () => {
          this.updateFilterSection('application', { isLoading: false });
        },
      });
  }

  loadDemandGenerator(searchTerm: string) {
    this.updateFilterSection('demandGenerator', { isLoading: true });
    const queryParams = {
      ...(searchTerm && { keyword: searchTerm }),
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('demandGenerator')?.pagination.pageSize,
      pageNumber:
        this.getFilterSection('demandGenerator')?.pagination.pageNumber,
      cfApplicationCode: this.selectedFilters?.application || '',
    };
    this.ssoService
      .getSharedURL(FILTER_DEMAND_GENERATOR, queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            this.updateFilterSection('demandGenerator', {
              options: resp.data.response,
              isLoading: false,
            });
          } else {
            this.updateFilterSection('demandGenerator', { isLoading: false });
          }
        },
        error: () => {
          this.updateFilterSection('demandGenerator', {
            options: [],
            isLoading: false,
          });
        },
      });
  }

  fetchMoreDemandGenerator() {
    this.updateFilterSection('demandGenerator', { isLoading: true });
    const section = this.getFilterSection('demandGenerator');
    this.incrementPage('demandGenerator');

    const queryParams: any = {
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: section?.pagination.pageSize,
      pageNumber:
        this.getFilterSection('demandGenerator')?.pagination.pageNumber,
      cfApplicationCode: this.selectedFilters?.application || '',
    };
    if (section?.searchTerm) {
      queryParams.keyword = section.searchTerm;
    }

    this.ssoService
      .getSharedURL(FILTER_DEMAND_GENERATOR, queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp) => {
          if (resp?.status) {
            const existing =
              this.getFilterSection('demandGenerator')?.options || [];
            this.updateFilterSection('demandGenerator', {
              options: [...existing, ...resp.data.response],
              isLoading: false,
            });
          } else {
            this.updateFilterSection('demandGenerator', { isLoading: false });
          }
        },
        error: () => {
          this.updateFilterSection('demandGenerator', { isLoading: false });
        },
      });
  }

  loadBatteryCapacity(searchTerm: string): void {
    this.updateFilterSection('batteryCapacity', { isLoading: true });
    const queryParams = {
      ...(searchTerm && { keyword: searchTerm }),
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: this.getFilterSection('batteryCapacity')?.pagination.pageSize,
      pageNumber:
        this.getFilterSection('batteryCapacity')?.pagination.pageNumber,
    };

    this.ssoService
      .getSharedURLAsText(FILTER_BATTERY_CAPACITY, queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (raw: string) => {
          const safe = raw.replaceAll(
            /:(\s*)(-?\d+\.\d+)(?=[,}\]\s])/g,
            ':$1"$2"'
          );
          const resp = JSON.parse(safe);

          if (resp?.status) {
            this.updateFilterSection('batteryCapacity', {
              options: resp.data.response.batteryCapacities,
              isLoading: false,
            });
          } else {
            this.updateFilterSection('batteryCapacity', { isLoading: false });
          }
        },
        error: () => {
          this.updateFilterSection('batteryCapacity', {
            options: [],
            isLoading: false,
          });
        },
      });
  }

  fetchMoreBatteryCapacity(): void {
    this.updateFilterSection('batteryCapacity', { isLoading: true });
    const section = this.getFilterSection('batteryCapacity');
    this.incrementPage('batteryCapacity');

    const queryParams: any = {
      vehicleTypeCode: this.commonService?.getVehicleTypeCode(),
      pageSize: section?.pagination.pageSize,
      pageNumber:
        this.getFilterSection('batteryCapacity')?.pagination.pageNumber,
    };
    if (section?.searchTerm) {
      queryParams.keyword = section.searchTerm;
    }

    this.ssoService
      .getSharedURLAsText(FILTER_BATTERY_CAPACITY, queryParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (raw: string) => {
          const safe = raw.replaceAll(
            /:(\s*)(-?\d+\.\d+)(?=[,}\]\s])/g,
            ':$1"$2"'
          );
          const resp = JSON.parse(safe);

          if (resp?.status) {
            const existing =
              this.getFilterSection('batteryCapacity')?.options || [];
            this.updateFilterSection('batteryCapacity', {
              options: [...existing, ...resp.data.response.batteryCapacities],
              isLoading: false,
            });
          } else {
            this.updateFilterSection('batteryCapacity', { isLoading: false });
          }
        },
        error: () => {
          this.updateFilterSection('batteryCapacity', {
            options: [],
            isLoading: false,
          });
        },
      });
  }

  // ─── Date Functionality ───────────────────────────────────────────────────

  /**
   * Returns the default date option (marked as default:true) from filterDateOptions.
   */
  getDefaultDate(): Option | null {
    // Delegated to UniversalFilterUtilityService — mirrors OEM filter default-date logic.
    return this.universalFilterUtility.getDefaultDate(this.filterDateOptions);
  }

  getDateOptionName(option?: Option | null): string {
    return option?.name ?? '';
  }

  shouldShowDateTooltip(option?: Option | null): boolean {
    return this.getDateOptionName(option).length > 18;
  }

  /**
   * Called when user selects a date option from the p-dropdown.
   * Opens the custom calendar dialog when 'custom' is selected.
   */
  getdateselection(ev: any): void {
    if (ev.value?.code === 'custom') {
      this.resetDate();
      this.customDate = true;
    } else {
      this.customDate = false;
    }

    if (
      this.filterDateOptions.options.length ===
      this.filterDateOptions.totalOption + 1
    ) {
      this.filterDateOptions.options.shift();
    }

    // No auto-emit; user must click Apply.
  }

  /**
   * Resets the date form control and closes the custom calendar dialog.
   */
  resetDate(): void {
    this.defaultDate = [null, null];
    this.selectedDate = [];
    this.filterForm.get('date')?.setValue(this.getDefaultDate());
    this.showTimePicker = false;
    this.resetFilterDateOptions();
  }

  /**
   * Trims extra custom date options so only the original preset options remain.
   */
  resetFilterDateOptions(): void {
    // Trimming logic centralised in UniversalFilterUtilityService.
    this.filterDateOptions = this.universalFilterUtility.resetFilterDateOptions(
      this.filterDateOptions
    );
  }

  /**
   * Called on each calendar date selection to track the selected range.
   */
  // selectionDate(ev: Date | null): void {
  //   this.defaultDate = this.selectedDate;
  //   this.diff = 0;
  //   const [start, end] = this.selectedDate;

  //   if (this.filterConfig?.time !== false) {
  //     this.showTimePicker = start && end ? true : false;
  //   } else {
  //     if (start) {
  //       this.selectedDate[0] = moment(start).startOf('day').toDate();
  //     }
  //     if (end) {
  //       this.selectedDate[1] = moment(end).endOf('day').toDate();
  //     }
  //   }

  //   if (this.selectedDate[1] != null) {
  //     const a = moment(this.selectedDate[0]);
  //     const b = moment(this.selectedDate[1]);
  //     this.diff = b.diff(a, 'days') + 1;
  //   }

  //   this.onDateSelected();
  // }

  selectionDate(ev: Date | null): void {
    // Match OEM: reference assignment so handleTimeChange mutations are reflected here too
    this.defaultDate = this.selectedDate;
    this.diff = 0;

    const [start, end] = this.selectedDate;

    if (this.filterConfig?.time !== false) {
      // Time picker is enabled — show it when both dates are selected.
      // handleTimeChange will apply the correct 00:00:00 / 23:59:59 defaults.
      this.showTimePicker = start && end ? true : false;
    } else {
      // Time picker is disabled — normalize in-place (mirrors OEM behaviour).
      if (start) {
        this.selectedDate[0] = moment(start).startOf('day').toDate();
      }
      if (end) {
        this.selectedDate[1] = moment(end).endOf('day').toDate();
      }
    }

    // Use the (possibly mutated) array elements directly — no new array created.
    // Creating a new array here breaks PrimeNG Calendar's range-mode ngModel
    // state (it expects [day] for start-only, not [day, undefined]).
    if (this.selectedDate[1] != null) {
      const a = moment(this.selectedDate[0]);
      const b = moment(this.selectedDate[1]);
      this.diff = b.diff(a, 'days') + 1;
    }

    this.onDateSelected();
  }

  // selectionDate() {
  //   if (this.selectedDateRange?.length === 2) {
  //     const start = new Date(this.selectedDateRange[0]);
  //     const end = new Date(this.selectedDateRange[1]);

  //     this.diff =
  //       Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) +
  //       1;

  //     this.showTimePicker = true;
  //     this.onDateSelected();
  //   }
  // }

  /**
   * Triggers edge-color styling on the calendar after selection.
   */
  onDateSelected(): void {
    calenderDateEdgeColor();
  }

  formatDateTime24(date: Date): string {
    if (!date) return '';

    const pad = (num: number) => num.toString().padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());

    const hours = pad(date.getHours()); // 24-hour format
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * Confirms the custom date range, creates a custom option, and emits the filter.
   *
   * Mirrors OEM filter's okDate() exactly:
   * - Normalization (startOf/endOf day) is already handled in selectionDate()
   *   for time-disabled configs.
   * - For time-enabled configs (e.g. Vehicle Data with time:true), handleTimeChange
   *   fires automatically when showTimePicker becomes true (same-day defaults:
   *   00:00:00 start, 23:59:59 end) — so no extra normalization is needed here.
   */
  okDate(): void {
    const startDate = moment(this.selectedDate[0]).format(
      'YYYY-MM-DD HH:mm:ss'
    );
    const endDate = moment(this.selectedDate[1]).format('YYYY-MM-DD HH:mm:ss');

    // const startDate = this.formatDateTime24(this.selectedDate[0]);
    // const endDate = this.formatDateTime24(this.selectedDate[1]);

    const obj: any = {
      code: 'custom1',
      name:
        moment(this.selectedDate[0]).format('DD-MM-YYYY hh:mm:ss A') +
        ' to ' +
        moment(this.selectedDate[1]).format('DD-MM-YYYY hh:mm:ss A'),
      id: 'custom1',
      startDate,
      endDate,
    };

    this.defaultDate = this.selectedDate;
    if (
      this.selectedDate &&
      this.selectedDate.length === 2 &&
      this.selectedDate[1]
    ) {
      const [start, end] = this.selectedDate;
      const startMoment = moment(start);
      const endMoment = moment(end);

      if (endMoment.isBefore(startMoment)) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Invalid Date Range',
          detail: 'End date/time cannot be before the start date/time',
        });
        return;
      }
    }

    this.filterDateOptions.options = [obj, ...this.filterDateOptions.options];
    this.filterForm.controls['date'].setValue(
      this.filterDateOptions.options[0]
    );

    this.customDate = false;
    this.showTimePicker = false;
  }

  /**
   * Handles time changes from the time-selection component.
   */
  handleTimeChange(event: any): void {
    const clamp = (val: number, min: number, max: number) =>
      Math.min(Math.max(val, min), max);

    const startHour = clamp(event.startTime.startHour, 0, 23);
    const startMinute = clamp(event.startTime.startMinute, 0, 59);
    const startSecond = clamp(event.startTime.startSecond, 0, 59);
    const endHour = clamp(event.endTime.endHour, 0, 23);
    const endMinute = clamp(event.endTime.endMinute, 0, 59);
    const endSecond = clamp(event.endTime.endSecond, 0, 59);

    if (this.selectedDate[0]) {
      this.selectedDate[0] = moment(this.selectedDate[0])
        .set({
          hour: startHour,
          minute: startMinute,
          second: startSecond,
          millisecond: 0,
        })
        .toDate();
    }
    if (this.selectedDate[1]) {
      this.selectedDate[1] = moment(this.selectedDate[1])
        .set({
          hour: endHour,
          minute: endMinute,
          second: endSecond,
          millisecond: 0,
        })
        .toDate();
    }
  }

  // handleTimeChange(event: any) {
  //   this.selectedStartTime = event.startTime;
  //   this.selectedEndTime = event.endTime;
  // }

  @HostListener('document:mousedown', ['$event'])
  handleDocumentClick(event: MouseEvent) {
    if (!this.isAdvancedOpen) return;

    const target = event.target as HTMLElement;

    const clickedInsideOverlay = target.closest('.universal-advanced-panel');

    const clickedInsideNgSelect =
      target.closest('.ng-dropdown-panel') ||
      target.closest('.ng-select-container');

    const clickedToggleButton = target.closest('.filter-toggle-btn');

    if (clickedInsideOverlay || clickedInsideNgSelect || clickedToggleButton) {
      return;
    }

    this.advancedPanel.hide();
  }

  private getDefaultMonthRange(): MonthRange {
    const start = moment(this.currentMonth).startOf('month').toDate();
    return { start, end: start };
  }

  /** Accepts new {start,end} shape or legacy single-date/ISO string (migration safety). */
  private parseSavedMonthRange(saved: any): MonthRange | null {
    if (!saved) return null;

    if (saved.start && saved.end) {
      const start = moment(saved.start).startOf('month').toDate();
      const end = moment(saved.end).startOf('month').toDate();
      return isNaN(start.getTime()) || isNaN(end.getTime())
        ? null
        : { start, end };
    }

    const legacy = new Date(saved);
    if (!isNaN(legacy.getTime())) {
      const start = moment(legacy).startOf('month').toDate();
      return { start, end: start };
    }
    return null;
  }

  // ─────────────────────────────────────────────────────────────────────────────

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.removeDocumentListener) {
      this.removeDocumentListener();
    }
  }
}
